import { useState } from 'react';
import { useCreateProject } from '../hooks/useApi';
import { ClientAutocomplete } from './ClientAutocomplete';

/**
 * Modal de creación de proyecto. Reusable desde la página `/projects` y desde
 * el CTA "+ Nuevo proyecto" del Sidebar (decisión 2026-04-29: discoverabilidad).
 */
export function CreateProjectModal({
  onClose,
  onCreated,
  defaultClientId = null,
}: {
  onClose: () => void;
  onCreated?: (slug: string) => void;
  /** Pre-seleccionar un cliente (cuando se crea proyecto desde la vista de un cliente). */
  defaultClientId?: string | null;
}) {
  const [name, setName] = useState('');
  const [clientId, setClientId] = useState<string | null>(defaultClientId);
  const [description, setDescription] = useState('');
  const create = useCreateProject();

  return (
    <div
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={async (e) => {
          e.preventDefault();
          if (!name.trim()) return;
          const project = await create.mutateAsync({
            name,
            clientId: clientId ?? undefined,
            description: description || undefined,
          });
          onCreated?.(project.slug);
          onClose();
        }}
        className="bg-slate-900 border border-slate-800 rounded-2xl p-6 w-full max-w-md space-y-4"
      >
        <h2 className="text-xl font-semibold">Nuevo proyecto</h2>
        <input
          autoFocus
          placeholder="Nombre"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2"
        />
        <ClientAutocomplete
          value={clientId}
          onChange={setClientId}
          placeholder="Cliente (autocompleta o crea nuevo inline)"
        />
        <textarea
          placeholder="Descripción (opcional)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full bg-slate-950 border border-slate-700 rounded-lg p-2 h-24 resize-none"
        />
        {create.error && (
          <div className="text-sm text-red-400">{(create.error as Error).message}</div>
        )}
        <div className="flex justify-end gap-2">
          <button type="button" onClick={onClose} className="px-3 py-2 text-slate-400">
            Cancelar
          </button>
          <button
            type="submit"
            disabled={create.isPending || !name.trim()}
            className="px-4 py-2 bg-vision-dark hover:bg-vision rounded-lg disabled:opacity-50"
          >
            {create.isPending ? 'Creando…' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
}
